// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { horizontalBars, fuelLegendHtml, el } from '../components/charts';
import { squarify } from '../utils/squarify';
import { FUEL_COLOUR, FUEL_ORDER, type Fuel } from '../fuels';
import type { MakeMix } from '../types';
import { attachSvgZoom } from '../utils/svgZoom';
import { formatCompact, formatNumber, formatPercent, titleCase } from '../utils/format';

/** Dominant fuel of a make (for treemap colour). */
function dominantFuel(m: MakeMix): Fuel {
  let best: Fuel = 'petrol';
  let bestV = -1;
  for (const f of FUEL_ORDER) {
    if (m[f] > bestV) {
      bestV = m[f];
      best = f;
    }
  }
  return best;
}

export function renderMakes(root: HTMLElement, ctx: ViewContext): void {
  const { national } = ctx.data;
  const makes = national.makes;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Makes &amp; models</h1>
      <p class="view-sub">What Australians actually drive. Toyota alone is more than one in five passenger cars — and the
        electric market is dominated by a single brand. Each rectangle is a make, sized by cars on the road and coloured
        by its most common fuel.</p>
    </div>

    <div class="panel" style="margin-bottom:var(--space-xl)">
      <div class="panel-title">The passenger fleet by make</div>
      <div class="panel-sub">Top ${makes.length} makes. Drag to pan, scroll to zoom, double-click to reset. Hover for the fuel split.</div>
      <div class="chart-frame" data-role="tree"></div>
      ${fuelLegendHtml(FUEL_ORDER)}
    </div>

    <div class="grid-2 wide">
      <div class="panel">
        <div class="panel-title">Most popular models</div>
        <div class="panel-sub">All fuels, passenger vehicles.</div>
        <div class="chart-scroll" data-role="models"></div>
      </div>
      <div class="panel">
        <div class="panel-title">Australia's electric cars, by model</div>
        <div class="panel-sub">Battery-electric passenger vehicles on the road.</div>
        <div class="chart-scroll" data-role="ev"></div>
      </div>
    </div>
  `;

  // ── Treemap ──
  const treeSlot = root.querySelector('[data-role="tree"]') as HTMLElement;
  const W = 960;
  const H = 460;
  const rects = squarify(makes.map((m) => m.total), W, H);
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${W} ${H}`, width: W, height: H, role: 'img', preserveAspectRatio: 'xMidYMid meet' });
  makes.forEach((m, i) => {
    const r = rects[i];
    if (r.w <= 0 || r.h <= 0) return;
    const fuel = dominantFuel(m);
    const g = el('g');
    const tip = `${titleCase(m.make)}\n${formatNumber(m.total)} cars (${formatPercent((m.total / national.passengerMix.total) * 100)})\n${FUEL_ORDER.filter((f) => m[f] > 0).map((f) => `${f}: ${formatPercent((m[f] / m.total) * 100)}`).join(' · ')}`;
    g.appendChild(el('rect', { x: r.x, y: r.y, width: r.w, height: r.h, fill: FUEL_COLOUR[fuel], stroke: '#ffffff', 'stroke-width': 1.2, 'data-tip': tip, 'aria-label': tip, style: 'cursor:default' }));
    if (r.w > 54 && r.h > 22) {
      const t = el('text', { x: r.x + 5, y: r.y + 15, 'font-size': 11, 'font-weight': 700, fill: fuel === 'other' ? '#0f172a' : '#ffffff', style: 'pointer-events:none' });
      t.textContent = titleCase(m.make);
      g.appendChild(t);
      if (r.h > 34) {
        const t2 = el('text', { x: r.x + 5, y: r.y + 29, 'font-size': 10, fill: fuel === 'other' ? '#475569' : 'rgba(255,255,255,0.85)', style: 'pointer-events:none', 'font-family': 'var(--font-mono)' });
        t2.textContent = formatCompact(m.total);
        g.appendChild(t2);
      }
    }
    svg.appendChild(g);
  });
  treeSlot.appendChild(svg);
  attachSvgZoom(svg, { maxScale: 10 });

  // ── Top models ──
  const modelsSlot = root.querySelector('[data-role="models"]') as HTMLElement;
  modelsSlot.appendChild(
    horizontalBars(
      national.topModels.slice(0, 15).map((m) => ({ id: m.name, label: titleCase(m.name), value: m.count, colour: '#475569', tip: `${titleCase(m.name)}: ${formatNumber(m.count)} on the road` })),
      { width: 520, rowHeight: 26, labelWidth: 200, valueFormat: (v) => formatCompact(v) },
    ),
  );

  // ── EV models ──
  const evSlot = root.querySelector('[data-role="ev"]') as HTMLElement;
  evSlot.appendChild(
    horizontalBars(
      national.bevModels.slice(0, 15).map((m) => ({ id: m.name, label: titleCase(m.name), value: m.count, colour: FUEL_COLOUR.bev, tip: `${titleCase(m.name)}: ${formatNumber(m.count)} battery-electric on the road` })),
      { width: 520, rowHeight: 26, labelWidth: 200, valueFormat: (v) => formatCompact(v) },
    ),
  );
}
